import React from 'react';
import { Dropdown } from 'react-bootstrap';

import SoftBadge from 'components/common/SoftBadge';
import CardDropdown from 'components/common/CardDropdown';

/**
 * @function UsersColumns
 * @description
 * Genera la configuración de columnas para la tabla de usuarios certificados.
 * Incluye columnas como nombre, email, estatus, tipo, y acciones (editar, eliminar).
 *
 * @param {function} handleEditUser - Función que se ejecuta cuando se hace clic en "Editar".
 * @param {function} handleDeleteUser - Función que se ejecuta cuando se hace clic en "Eliminar".
 *
 * @returns {Array<Object>} Arreglo de objetos que representan las columnas para un componente de tabla compatible con React Table.
 *
 * @example
 * const columns = UsersColumns(onEdit, onDelete);
 */

const UsersColumns = (handleEditUser, handleDeleteUser) => [
    {
        accessor: 'index',
        Header: '#',
        headerProps: {
            style: { width: '30px', minWidth: '30px', maxWidth: '50px' }
        },
        cellProps: {
            style: { width: '30px', minWidth: '30px', maxWidth: '50px' }
        },
        Cell: rowData => {
            const { index } = rowData.row.original;
            return index+1;
        }
    },
    {
        accessor: 'id',
        Header: 'ID',
        // Columna oculta utilizada internamente para identificadores únicos
        headerProps: { className: 'hidden-column' },
        cellProps: { className: 'hidden-column' },
    },
    {
        accessor: 'name',
        Header: 'Nombre',
        // Columna que muestra el nombre completo del usuario con un enlace
        headerProps: { className: 'ps-2', style: { height: '50px' } },
        cellProps: { className: 'white-space-nowrap pe-5 ps-2 py-2 fw-bold' }
    },
    {
        accessor: 'username',
        Header: 'Username',
        // Columna para el nombre de usuario
        headerProps: {
            className: 'ps-2',
            style: { width: '150px', minWidth: '150px', maxWidth: '200px' }
        },
        cellProps: {
            className: 'ps-2',
            style: { width: '150px', minWidth: '150px', maxWidth: '200px' }
        }
    },
    {
        accessor: 'email',
        Header: 'Email',
        // Columna para mostrar el correo electrónico
        headerProps: { className: 'ps-2', style: { minWidth: '200px' } },
        cellProps: { className: 'ps-2' }
    },
    {
        accessor: 'type',
        Header: 'Tipo',
        // Columna para mostrar el rol del usuario (Administrador, Examinador)
        headerProps: {
            className: 'ps-2',
            style: { width: '150px', minWidth: '150px', maxWidth: '200px' }
        },
        cellProps: {
            className: 'ps-2',
            style: { width: '150px', minWidth: '150px', maxWidth: '200px' }
        }
    },
    {
        accessor: 'status',
        Header: 'Estatus',
        // Columna que muestra el estado actual del usuario con un badge visual
        headerProps: {
            className: 'text-center white-space-nowrap',
            style: { width: '100px', minWidth: '100px', maxWidth: '100px' }
        },
        cellProps: {
            className: 'font-sans-serif fs-0 text-center',
            style: { width: '100px', minWidth: '100px', maxWidth: '100px' }
        },
        Cell: rowData => {
            const { status } = rowData.row.original;
            return status ? (
                <SoftBadge bg={status.type}>{status.content}</SoftBadge>
            ) : (
                <p className="mb-0 text-500 font-sans-serif fs--1">N/A</p>
            );
        }
    },
    {
        accessor: 'none',
        Header: '',
        disableSortBy: true,
        // Columna de acciones (Editar/Eliminar)
        headerProps: {
            style: { width: '60px', minWidth: '60px', maxWidth: '60px' }
        },
        cellProps: {
            className: 'text-end',
            style: { width: '60px', minWidth: '60px', maxWidth: '60px' }
        },
        Cell: rowData => {
            const { id, username } = rowData.row.original;
            return (
                <CardDropdown>
                    <div className="py-2">
                        <Dropdown.Item onClick={() => handleEditUser(id)}>Editar</Dropdown.Item>
                        <Dropdown.Item className="text-danger" onClick={() => handleDeleteUser(id, username)}>
                            Eliminar
                        </Dropdown.Item>
                    </div>
                </CardDropdown>
            );
        }
    }
];

export default UsersColumns;

/*
    Cell: rowData => {
        const { name } = rowData.row.original;
        return (
            <Flex alignItems="center" className="position-relative py-1">
                <h6 className="mb-0">
                    <Link to="/support-desk/contact-details" className="stretched-link text-900">
                        {name}
                    </Link>
                </h6>
            </Flex>
        );
    }
*/